import type { AppError } from "@/shared/api";

/**
 * Канал показу gallery-помилки (spec §7.4):
 * - `inline` — FormAlert над сіткою / у тілі діалогу;
 * - `toast`  — foreground toast (403/unknown);
 * - `reorder` — toast.error + revert snapshot (PUT order);
 * - `silent`  — D9: 404 photo_not_found при delete → тихий успіх.
 */
export type GalleryErrorSurface = "inline" | "toast" | "reorder" | "silent";

/** Типобезпечні i18n-ключі gallery-помилок. */
export type GalleryErrorKey =
  | "salon.gallery.errors.full"
  | "salon.gallery.errors.unsupportedType"
  | "salon.gallery.errors.invalidImage"
  | "salon.gallery.errors.tooLarge"
  | "salon.gallery.errors.reorderFailed"
  | "salon.gallery.errors.uploadFailed"
  | "errors.network"
  | "errors.server"
  | "errors.forbidden"
  | "errors.unknown";

export interface MappedGalleryError {
  surface: GalleryErrorSurface;
  i18nKey?: GalleryErrorKey;
}

/**
 * Чиста `AppError → {surface, i18nKey}` (spec §7.4). Гілкується по `code`
 * (бо 413/415 колапсують у `kind=server` — лише `code` їх розрізняє).
 * `photo_not_found` → `silent` (D9: трактуємо як вже видалене).
 * `invalid_photo_order` → `reorder` (toast+revert у composable).
 */
export function mapGalleryError(e: AppError): MappedGalleryError {
  switch (e.kind) {
    case "conflict":
      if (e.code === "gallery_full") {
        return { surface: "inline", i18nKey: "salon.gallery.errors.full" };
      }
      return { surface: "toast", i18nKey: "errors.unknown" };

    case "validation":
      if (e.code === "invalid_image") {
        return { surface: "inline", i18nKey: "salon.gallery.errors.invalidImage" };
      }
      if (e.code === "invalid_photo_order") {
        return { surface: "reorder", i18nKey: "salon.gallery.errors.reorderFailed" };
      }
      // invalid_body / invalid_id / решта 400/422
      return { surface: "inline", i18nKey: "salon.gallery.errors.uploadFailed" };

    case "notFound":
      if (e.code === "photo_not_found") {
        return { surface: "silent" };
      }
      return { surface: "toast", i18nKey: "errors.unknown" };

    case "server":
      if (e.code === "unsupported_image_type") {
        return { surface: "inline", i18nKey: "salon.gallery.errors.unsupportedType" };
      }
      if (e.code === "payload_too_large") {
        return { surface: "inline", i18nKey: "salon.gallery.errors.tooLarge" };
      }
      return { surface: "inline", i18nKey: "errors.server" };

    case "network":
      return { surface: "inline", i18nKey: "errors.network" };

    case "forbidden":
      return { surface: "toast", i18nKey: "errors.forbidden" };

    default:
      return { surface: "toast", i18nKey: "errors.unknown" };
  }
}
